import { Button, FormErrorMessage } from '@tryhackme/thm-ui-components';
import type { ButtonColors } from '@tryhackme/thm-ui-components/dist/components/atoms/button/button.types';
import type React from 'react';
import { useReducer, useRef } from 'react';
import { StyledFileUploadInput } from 'src/common/components/upload-file/upload-file.styles';
import type { FileValidators, HandleFileUploadFunction, UploadFileReducerAction, UploadFileReducerState } from 'src/common/components/upload-file/upload-file.types';
import { validateFileFormat, validateFileSize } from 'src/common/components/upload-file/validators';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { StyledErrorText } from 'src/features/management-dashboard/management-dashboard.styles';

/**
 * This is a generic component that can be used to upload files to your client.
 * It enables inversion of control by allowing you to pass in a function that will handle the file upload and to do
 * whatever you desire with the file(s) and event.
 * It also allows you to pass in an array of file validators that will be run before the file upload is triggered
 * meeting whatever needs you may have.
 *
 * @remarks The component may be extended modified if further functionality is required. Like adding a dynamic reducer to
 *  extend the logic of the component, or a custom style of the Button component.
 *
 * @param handleFileUpload - This is the function that will be called when the user selects a file to upload.
 * @param onValidationError - Call this function if provided when file validation has failed
 * @param hideError - In some instance, error rendering may be handled outside of the component (eg. forms)
 * @param fileValidators - This is an array of file validators that will be run before the file upload is triggered.
 * @param children - This is the text or any React Node that will be displayed on the button.
 * @param accept - File types allowed in selector
 * @param restOfNativeInputProps - This is the rest of the props that will be passed to the input element.
 * @param buttonColour - Controls colour of button, e.g. primary, secondary etc
 * @param disabled - Allows disabling of the button outside the component
 */
export const UploadFile: React.FC<{
  handleFileUpload: HandleFileUploadFunction;
  onValidationError?: (error: string) => void;
  hideError?: boolean;
  fileValidators?: FileValidators;
  buttonColour?: ButtonColors;
  children?: React.ReactNode;
  disabled?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>> = ({
  handleFileUpload,
  onValidationError,
  hideError,
  fileValidators = [validateFileSize(), validateFileFormat()],
  children = 'Upload a file',
  accept = '.png, .jpg, .jpeg, .svg',
  buttonColour = 'primary',
  disabled,
  ...restOfNativeInputProps
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [{
    status,
    error
  }, setState] = useReducer(UploadFileReducer, {
    status: 'idle'
  });

  /*+
   * By default this function will expose you a callback that will be called when the user selects a file(s) to upload.
   * It also presents a layer of error handling wrapping the file upload function in a try/catch.
   */
  const onFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      files
    } = event.target;
    if (!files?.length) return;
    for (const validator of fileValidators ?? []) {
      const validations = [...files].map(element => validator(element));
      if (!validations.every(v => v.valid)) {
        const errors = validations.filter(v => !v.valid).map(v => v.error).join(', ');
        setState({
          type: 'error',
          payload: errors ?? 'Invalid file.'
        });
        onValidationError?.(errors);
        return;
      }
    }
    setState({
      type: 'uploading'
    });
    try {
      await handleFileUpload(files, event);
      setState({
        type: 'idle'
      });
    } catch (uploadError: unknown) {
      const errorMsg = isQueryErrorWithMessage(uploadError) ? uploadError.data.message : (uploadError as Error)?.message ?? 'Something went wrong. Please try again.';
      setState({
        type: 'error',
        payload: errorMsg
      });
    }
  };
  const isUploading = status === 'uploading';
  return <div data-sentry-component="UploadFile" data-sentry-source-file="upload-file.tsx">
      <Button size="default" variant="outlined" color={buttonColour} onClick={() => inputRef.current?.click()} disabled={isUploading || disabled} data-sentry-element="Button" data-sentry-source-file="upload-file.tsx">
        {isUploading ? 'Uploading...' : children}
        <StyledFileUploadInput accept={accept} name="upload-file" {...restOfNativeInputProps} data-testid="upload-file-input" ref={inputRef} type="file"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onChange={onFileInputChange} hidden data-sentry-element="StyledFileUploadInput" data-sentry-source-file="upload-file.tsx" />
      </Button>
      {!!error && !hideError && <FormErrorMessage>
          <StyledErrorText>{error}</StyledErrorText>
        </FormErrorMessage>}
    </div>;
};

// We use useReducer to manage the state of the component as different pieces change together. Cleaner than useState.
const UploadFileReducer: React.Reducer<UploadFileReducerState, UploadFileReducerAction> = (state, action) => {
  switch (action.type) {
    case 'idle':
      {
        return {
          status: 'idle',
          error: undefined
        };
      }
    case 'uploading':
      {
        return {
          status: 'uploading',
          error: undefined
        };
      }
    case 'error':
      {
        return {
          status: 'error',
          error: action.payload
        };
      }
    default:
      {
        return state;
      }
  }
};